import { StateMachine } from "@/core/arch/state_machine";
import type { JobRecord, JobStatus } from "./types";

// ── State ─────────────────────────────────────────────────────────────────────

export type IdentityBackfillJobPhase =
  | { kind: "idle" }
  | { kind: "kicking_off" }
  | { kind: "watching"; job: JobRecord }
  | { kind: "cancelling"; job: JobRecord }
  | { kind: "error"; message: string };

export interface IdentityBackfillJobState {
  phase: IdentityBackfillJobPhase;
}

// ── Repository interface ──────────────────────────────────────────────────────

export interface IdentityBackfillKickoffOptions {
  allowFromUnrecoverable?: boolean;
}

export interface IdentityBackfillJobRepository {
  kickoff(options?: IdentityBackfillKickoffOptions): Promise<string>;
  findRunningJob(): Promise<JobRecord | null>;
  cancelJob(jobId: string): Promise<void>;
  subscribeToJob(
    jobId: string,
    onUpdate: (job: JobRecord) => void,
    onError: (err: Error) => void,
  ): () => void;
}

// ── BLoC ─────────────────────────────────────────────────────────────────────

const JOB_TYPE = "identity-backfill-dedupe";

export class IdentityBackfillJobBloc extends StateMachine<IdentityBackfillJobState> {
  private _unsubscribe: (() => void) | null = null;

  constructor(private readonly repo: IdentityBackfillJobRepository) {
    super({ phase: { kind: "idle" } });
    this.appendDisposeCallback(() => this._detach());
  }

  /**
   * Called on mount. If a job of this type is already running, attach to it
   * instead of showing the idle "Run" prompt.
   */
  async resumeIfRunning(): Promise<void> {
    try {
      const job = await this.repo.findRunningJob();
      if (job) {
        this._attach(job);
      }
    } catch {
      // Non-fatal — user sees idle state and can kick off manually.
    }
  }

  async kickoff(options?: IdentityBackfillKickoffOptions): Promise<void> {
    this.setState({ phase: { kind: "kicking_off" } });
    try {
      const jobId = await this.repo.kickoff(options);
      const seedJob: JobRecord = {
        id: jobId,
        type: JOB_TYPE,
        status: "pending",
        triggeredBy: "",
        total: null,
        processed: 0,
        counters: null,
        result: null,
        error: null,
        startedAt: null,
        completedAt: null,
      };
      this._attach(seedJob);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.setState({ phase: { kind: "error", message } });
    }
  }

  async cancel(): Promise<void> {
    const phase = this.state.phase;
    if (phase.kind !== "watching") return;
    this.setState({ phase: { kind: "cancelling", job: phase.job } });
    try {
      await this.repo.cancelJob(phase.job.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.setState({ phase: { kind: "error", message } });
    }
  }

  reset(): void {
    this._detach();
    this.setState({ phase: { kind: "idle" } });
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private _attach(seed: JobRecord): void {
    this._detach();
    this.setState({ phase: { kind: "watching", job: seed } });

    this._unsubscribe = this.repo.subscribeToJob(
      seed.id,
      (job) => {
        const current = this.state.phase;
        const isTerminal =
          job.status === "complete" ||
          job.status === "failed" ||
          job.status === "cancelled";
        if (current.kind === "cancelling" && !isTerminal) {
          this.setState({ phase: { kind: "cancelling", job } });
          return;
        }
        this.setState({ phase: { kind: "watching", job } });
      },
      (err) => {
        this.setState({
          phase: { kind: "error", message: err.message },
        });
      },
    );
  }

  private _detach(): void {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  /** Visible status from the current job record, or null when idle/kicking off. */
  get jobStatus(): JobStatus | null {
    const { phase } = this.state;
    if (phase.kind === "watching" || phase.kind === "cancelling") {
      return phase.job.status;
    }
    return null;
  }
}
